import { Location } from "history";
import { Action } from "redux";

import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import {
    BlogsFeedCategory,
    ScrollToPosition,
} from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeedPage";
import { PageRef } from "@/mk2/apps/forum/schemas";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";
import { BlogPostType } from "@/mk2/schemas";

///////////////////////////////////////////////////////////////////
// Fetch blogs feeds

export interface BlogsFeedFetchAction extends Action {
    readonly pageRef: PageRef;
    readonly category: BlogsFeedCategory;
    readonly postType: BlogPostType;
    readonly query: string;
}

export const BLOGS_FEED_FETCH_TRIGGER = "BLOGS_FEED_FETCH_TRIGGER";
export interface BlogsFeedFetchTriggerAction extends BlogsFeedFetchAction {
    readonly type: typeof BLOGS_FEED_FETCH_TRIGGER;
    readonly currentUrl: Location;
    readonly numPostsBeforeLoad: number;
}
export const BLOGS_FEED_FETCH_REQUEST = "BLOGS_FEED_FETCH_REQUEST";
export interface BlogsFeedFetchRequestAction extends BlogsFeedFetchAction, APIRequestAction {
    readonly type: typeof BLOGS_FEED_FETCH_REQUEST;
}
export interface BlogsFeedFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        complaints: number[];
        postsSubtreeComplaintList?: { [postId: number]: number[] };
        posts: number[];
    };
}
export const BLOGS_FEED_FETCH_SUCCESS = "BLOGS_FEED_FETCH_SUCCESS";
export interface BlogsFeedFetchSuccessAction
    extends BlogsFeedFetchAction, APISuccessAction<BlogsFeedFetchNormalizedResponse> {
    readonly type: typeof BLOGS_FEED_FETCH_SUCCESS;
    readonly page: number;
    readonly hasComplaints: boolean;
    readonly hashtags: BoxedHashtags;
    readonly brandDaysPostId: number;
}
export const BLOGS_FEED_FETCH_FAILURE = "BLOGS_FEED_FETCH_FAILURE";
export interface BlogsFeedFetchFailureAction extends BlogsFeedFetchAction, APIFailureAction {
    readonly type: typeof BLOGS_FEED_FETCH_FAILURE;
}

export const blogsFeedFetchTrigger = (
    pageRef: PageRef,
    category: BlogsFeedCategory,
    postType: BlogPostType,
    query: string,
    currentUrl: Location,
    numPostsBeforeLoad: number = 0,
): BlogsFeedFetchTriggerAction => ({
    type: BLOGS_FEED_FETCH_TRIGGER,
    pageRef,
    category,
    postType,
    query,
    currentUrl,
    numPostsBeforeLoad,
});
export const blogsFeedFetchApi = {
    request: (
        pageRef: PageRef,
        category: BlogsFeedCategory,
        postType: BlogPostType,
        query: string,
    ): BlogsFeedFetchRequestAction => ({
        type: BLOGS_FEED_FETCH_REQUEST,
        pageRef,
        category,
        postType,
        query,
    }),
    success: (
        pageRef: PageRef,
        category: BlogsFeedCategory,
        postType: BlogPostType,
        query: string,
        page: number,
        response: BlogsFeedFetchNormalizedResponse,
        hasComplaints: boolean,
        hashtags: BoxedHashtags,
        brandDaysPostId: number,
    ): BlogsFeedFetchSuccessAction => ({
        type: BLOGS_FEED_FETCH_SUCCESS,
        pageRef,
        category,
        postType,
        query,
        page,
        response,
        hasComplaints,
        hashtags,
        brandDaysPostId,
    }),
    failure: (
        pageRef: PageRef,
        category: BlogsFeedCategory,
        postType: BlogPostType,
        query: string,
        error: any,
    ): BlogsFeedFetchFailureAction => ({
        type: BLOGS_FEED_FETCH_FAILURE,
        pageRef,
        category,
        postType,
        query,
        error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Blogs feed - scrollto

export const BLOGS_FEED_SCROLLTO_TRIGGER = "BLOG_FEED_SCROLLTO_TRIGGER";
export interface BlogsFeedScrollToTriggerAction {
    readonly type: typeof BLOGS_FEED_SCROLLTO_TRIGGER;
    readonly scrollto: ScrollToPosition;
}

export const blogsFeedScrollToTrigger = (
    scrollto: ScrollToPosition,
): BlogsFeedScrollToTriggerAction => ({
    type: BLOGS_FEED_SCROLLTO_TRIGGER,
    scrollto,
});
